/**
 * Turns a bundle's flat `blocks` array into ordered, absolute-indexed render
 * slots for one Workshop section (spec: taxonomy reconcile, render-from-data).
 *
 * `index` is the ABSOLUTE index into `base.blocks` so a field edits/removes the
 * exact block (multiple same-category blocks are individually addressable).
 * A `null` index is a not-yet-created "starter" slot for an essential category
 * with no block (create-on-first-edit). `ordinal` gives a stable per-category
 * key that survives the null→index transition when the starter creates a block.
 */
import type { PersonaBundleResponse } from '@/types/personaWizard'
import type { BlockCategory } from '@/stores/composerStore'
import type { BlockGroup } from './blockTaxonomy'

export interface BlockSlot {
  category: string
  index: number | null
  ordinal: number
}

// Canonical within-group category order (grouping/display only — RENDER
// order is owned by the composer's COMPOSER_LAYER_PRIORITY table).
const GROUP_ORDER: Record<BlockGroup, BlockCategory[]> = {
  who: ['identity'],
  how: ['trait', 'tone', 'behavioral'],
  rules: ['constraints', 'never_forget', 'escalation'],
  know: ['domain', 'examples', 'do_examples', 'dont_examples'],
  extra: ['output_format', 'freeform'],
}

const KNOWN = new Set<string>([
  ...GROUP_ORDER.who,
  ...GROUP_ORDER.how,
  ...GROUP_ORDER.rules,
  ...GROUP_ORDER.know,
  ...GROUP_ORDER.extra,
])

export function blockSlotsForGroup(
  base: PersonaBundleResponse | null,
  group: BlockGroup,
  starterCategories: BlockCategory[] = [],
): BlockSlot[] {
  const blocks = base?.blocks ?? []
  const slots: BlockSlot[] = []

  for (const category of GROUP_ORDER[group]) {
    const matches = blocks
      .map((b, index) => ({ category: b.category, index }))
      .filter((m) => m.category === category)
    if (matches.length === 0 && starterCategories.includes(category)) {
      slots.push({ category, index: null, ordinal: 0 })
    } else {
      matches.forEach((m, ordinal) => slots.push({ category, index: m.index, ordinal }))
    }
  }

  // Catch-all: unknown categories surface in the 'extra' section so nothing
  // a persona actually has can ever be invisible.
  if (group === 'extra') {
    const seen: Record<string, number> = {}
    blocks.forEach((b, index) => {
      if (!KNOWN.has(b.category)) {
        const ordinal = seen[b.category] ?? 0
        seen[b.category] = ordinal + 1
        slots.push({ category: b.category, index, ordinal })
      }
    })
  }

  return slots
}
